/**
 * Structural skeleton for generic detail pages.
 *
 * Mirrors the exact DetailView header layout so navigating from list → detail
 * produces zero layout shift. Every row matches the real component's height,
 * padding, and flex structure.
 *
 * Header rows (from DetailView.tsx):
 *   Row 1: backNav (breadcrumb) + belowTitle (InlineAssignments) — px-6 pt-4 pb-0
 *   Row 2: media + title + actions — px-6 pt-2 pb-4
 *   Row 3: belowActions (InlineShareActions) — px-6 pb-4
 *   Tab bar: sticky pills — pt-4 px-6
 *
 * Content area adapts via DocViewSchema: schema-aware doctypes get section
 * cards matching their schema count; others get a generic 3-card pattern.
 */

import { Skeleton } from "@/components/ui/skeleton";
import { hasDocViewSchema, getDocViewSchema } from "@/lib/views/docViewSchemas";

interface DetailPageSkeletonProps {
  doctype: string;
}

/** Content skeleton cards based on DocViewSchema sections or a generic fallback. */
function ContentSkeleton({ doctype }: { doctype: string }) {
  if (hasDocViewSchema(doctype)) {
    const schema = getDocViewSchema(doctype);
    const sections = schema.views.experience?.sections ?? [];
    const count = Math.max(2, Math.min(sections.length, 6));
    return (
      <div className="space-y-4 px-4">
        {Array.from({ length: count }, (_, i) => (
          <Skeleton
            key={i}
            className="w-full rounded-md"
            style={{ height: i === 0 ? 180 : i === count - 1 ? 80 : 120 }}
          />
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-4 px-4">
      <Skeleton className="h-[180px] w-full rounded-md" />
      <Skeleton className="h-[120px] w-full rounded-md" />
      <Skeleton className="h-[80px] w-full rounded-md" />
    </div>
  );
}

export function DetailPageSkeleton({ doctype }: DetailPageSkeletonProps) {
  // Only Item has a guaranteed image — generic DocTypes usually don't
  const showImagePlaceholder = doctype === "Item";

  return (
    <div className="h-full flex flex-col">
      {/* Header area — matches DetailView border-b container */}
      <div className="border-b border-border/40 overflow-hidden">
        {/* Row 1: breadcrumb + assignment avatars (matches px-6 pt-4 pb-0) */}
        <div className="flex items-center justify-between gap-4 px-6 pt-4 pb-0">
          {/* Breadcrumb: "← DocType" — real is ~14px text with 16px icon */}
          <Skeleton className="h-5 w-28 rounded-md" />
          {/* InlineAssignments: h-9 w-9 avatar circles */}
          <div className="flex items-center gap-1">
            <Skeleton className="h-9 w-9 rounded-full" />
          </div>
        </div>

        {/* Row 2: title + header actions (matches px-6 pt-2 pb-4) */}
        <div className="flex items-center justify-between gap-4 px-6 pt-2 pb-4">
          <div className="flex items-center gap-4 min-w-0">
            {/* 112×112 image — only for Item (has gallery); generic docs rarely have images */}
            {showImagePlaceholder && (
              <Skeleton className="h-[112px] w-[112px] rounded-md shrink-0" />
            )}
            {/* Title: real is text-[28px] font-bold = ~34px rendered height */}
            <div className="min-w-0">
              <Skeleton className="h-[34px] w-56 rounded-md" />
            </div>
          </div>
          {/* HeaderActions: 3 icon buttons (h-9 w-9) + Save button (h-9 w-~72px) */}
          <div className="flex items-center gap-2 shrink-0">
            <Skeleton className="h-9 w-9 rounded-sm" />
            <Skeleton className="h-9 w-9 rounded-sm" />
            <Skeleton className="h-9 w-9 rounded-sm" />
            <Skeleton className="h-9 w-[72px] rounded-sm" />
          </div>
        </div>

        {/* Row 3: InlineShareActions — single unified pill bar (h-9, rounded-full) */}
        <div className="px-6 pb-4">
          <Skeleton className="h-9 w-[340px] rounded-full" />
        </div>
      </div>

      {/* Tab bar — matches DetailView sticky wrapper (pt-4 + pills) */}
      <div className="sticky top-0 z-20 relative">
        <div className="relative pt-4 px-6">
          <div className="flex items-center gap-2">
            <Skeleton className="h-8 w-20 rounded-full" />
            <Skeleton className="h-8 w-24 rounded-full" />
            <Skeleton className="h-8 w-20 rounded-full" />
          </div>
        </div>
      </div>

      {/* Content area — matches DetailView flex-1 overflow-y-auto pb-5 space-y-4 */}
      <div className="flex-1 overflow-y-auto pb-5 space-y-4 pt-4">
        <ContentSkeleton doctype={doctype} />
      </div>
    </div>
  );
}
